/**
 * Matches page - browse, filter, and generate matches across investors and projects.
 */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Loader2,
  GitMerge,
  Sparkles,
  X,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import MatchCard from '../components/MatchCard';
import Typeahead from '../components/Typeahead';
import matchService from '../services/matchService';
import investorService from '../services/investorService';
import projectService from '../services/projectService';
import {
  Match,
  MatchListFilters,
  MatchStatus,
  MatchType,
  GenerateMatchRequest,
} from '../types/match';
import { Investor } from '../types/investor';
import { Project } from '../types/project';

const MATCH_TYPE_OPTIONS: Array<{ value: MatchType; label: string }> = [
  { value: 'investor_startup', label: 'Investor → Startup' },
  { value: 'investor_project', label: 'Investor → Project' },
  { value: 'project_startup_integrity', label: 'Project ↔ Startup Integrity' },
];

const STATUS_OPTIONS: Array<{ value: MatchStatus; label: string }> = [
  { value: 'suggested', label: 'Suggested' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'pursuing', label: 'Pursuing' },
  { value: 'introduced', label: 'Introduced' },
  { value: 'passed', label: 'Passed' },
];

type GenerateScope = 'investor' | 'project' | 'pair';

const MatchesPage: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<MatchListFilters>({});
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  // Generate dialog state
  const [genOpen, setGenOpen] = useState<boolean>(false);
  const [genScope, setGenScope] = useState<GenerateScope>('investor');
  const [genInvestorId, setGenInvestorId] = useState<number | undefined>(undefined);
  const [genProjectId, setGenProjectId] = useState<number | undefined>(undefined);
  const [generating, setGenerating] = useState<boolean>(false);

  // Manual-match dialog state — operator picks an investor and a
  // company (or project) explicitly. Submits to /matches/manual which
  // computes the score using the same engine + stamps source="manual".
  const [manualOpen, setManualOpen] = useState<boolean>(false);
  const [manualInvestor, setManualInvestor] = useState<Investor | null>(null);
  const [manualCompany, setManualCompany] = useState<{ id: number; name: string; industry?: string } | null>(null);
  const [manualCompanyOptions, setManualCompanyOptions] = useState<{ id: number; name: string; industry?: string }[]>([]);
  const [manualNotes, setManualNotes] = useState<string>('');
  const [manualSubmitting, setManualSubmitting] = useState<boolean>(false);

  // Lazy-load the company list on first open of the manual modal.
  // /companies/search needs a query, so we just fetch the first page.
  useEffect(() => {
    if (!manualOpen || manualCompanyOptions.length > 0) return;
    (async () => {
      try {
        const r = await (await import('../services/api')).default.get('/companies', {
          params: { size: 500, page: 1 },
        });
        const cos = (r.data?.companies || []).map((c: any) => ({
          id: c.id, name: c.name, industry: c.industry,
        }));
        setManualCompanyOptions(cos);
      } catch {
        // toast handled at submit if needed
      }
    })();
  }, [manualOpen, manualCompanyOptions.length]);

  const submitManualMatch = async () => {
    if (!manualInvestor) {
      toast.error('Pick an investor');
      return;
    }
    if (!manualCompany) {
      toast.error('Pick a company');
      return;
    }
    setManualSubmitting(true);
    try {
      const apiMod = await import('../services/api');
      const r = await apiMod.default.post('/matches/manual', {
        investor_id: manualInvestor.id,
        startup_id: manualCompany.id,
        notes: manualNotes.trim() || undefined,
      });
      toast.success(
        `Manual match created (score ${Math.round(r.data?.overall_score ?? 0)})`,
      );
      // Close modal + reset + refresh listing.
      setManualOpen(false);
      setManualInvestor(null);
      setManualCompany(null);
      setManualNotes('');
      const refreshed = await matchService.fetchMatches(queryFilters);
      if (refreshed.data) setMatches(refreshed.data.matches);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to create manual match');
    } finally {
      setManualSubmitting(false);
    }
  };

  const queryFilters = useMemo(() => filters, [filters]);

  // Client-side sort over the loaded match list. Backend already
  // sorts by score desc by default; this lets the operator flip to
  // alphabetical or chronological without a round-trip.
  const [sortMode, setSortMode] = useState<
    | 'score_desc' | 'score_asc'
    | 'investor_asc' | 'investor_desc'
    | 'target_asc' | 'target_desc'
    | 'created_desc' | 'created_asc'
  >('score_desc');

  const sortedMatches = useMemo(() => {
    const copy = matches.slice();
    const investorName = (m: Match) =>
      (m.investor?.full_name || m.investor?.firm_name || '').toLowerCase();
    const targetName = (m: Match) =>
      (m.startup?.name || m.project?.project_name || '').toLowerCase();
    copy.sort((a, b) => {
      switch (sortMode) {
        case 'score_desc': return (b.overall_score ?? 0) - (a.overall_score ?? 0);
        case 'score_asc':  return (a.overall_score ?? 0) - (b.overall_score ?? 0);
        case 'investor_asc': return investorName(a).localeCompare(investorName(b));
        case 'investor_desc': return investorName(b).localeCompare(investorName(a));
        case 'target_asc': return targetName(a).localeCompare(targetName(b));
        case 'target_desc': return targetName(b).localeCompare(targetName(a));
        case 'created_desc': return (b.created_at || '').localeCompare(a.created_at || '');
        case 'created_asc': return (a.created_at || '').localeCompare(b.created_at || '');
        default: return 0;
      }
    });
    return copy;
  }, [matches, sortMode]);

  // Initial load of investors + projects for filter dropdowns and gen dialog
  useEffect(() => {
    const loadOptions = async () => {
      const [invRes, projRes] = await Promise.all([
        investorService.fetchInvestors({ size: 200 }),
        projectService.fetchProjects({ size: 200 }),
      ]);
      if (invRes.data) setInvestors(invRes.data.investors);
      if (projRes.data) setProjects(projRes.data.projects);
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const result = await matchService.fetchMatches(queryFilters);
      if (result.error) {
        toast.error(result.error);
        setMatches([]);
      } else {
        setMatches(result.data?.matches || []);
      }
      setIsLoading(false);
    };
    load();
  }, [queryFilters]);

  const handleFilterChange = <K extends keyof MatchListFilters>(
    key: K,
    value: MatchListFilters[K] | ''
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' || value === undefined ? undefined : value,
    }));
  };

  const handleGenerate = async () => {
    const req: GenerateMatchRequest = {};
    if (genScope === 'investor' || genScope === 'pair') {
      if (!genInvestorId) {
        toast.error('Pick an investor first');
        return;
      }
      req.investor_id = genInvestorId;
    }
    if (genScope === 'project' || genScope === 'pair') {
      if (!genProjectId) {
        toast.error('Pick a project first');
        return;
      }
      req.project_id = genProjectId;
    }

    setGenerating(true);
    const result = await matchService.generateMatches(req);
    setGenerating(false);

    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success(`Generated ${result.data?.generated ?? 0} match(es)`);
    setGenOpen(false);

    // Refresh list
    setIsLoading(true);
    const refreshed = await matchService.fetchMatches(queryFilters);
    if (refreshed.data) setMatches(refreshed.data.matches);
    setIsLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Matches</h1>
            <p className="text-gray-600 mt-1">
              Three-way matching engine — investor fit scoring across startups and projects.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setManualOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              title="Pair an investor with a startup or project manually"
            >
              <span className="mr-1.5 text-base leading-none">✦</span>
              Manual match
            </button>
            <button
              onClick={() => setGenOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate matches
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <select
              value={filters.match_type || ''}
              onChange={(e) =>
                handleFilterChange('match_type', e.target.value as MatchType | '')
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All match types</option>
              {MATCH_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={filters.status || ''}
              onChange={(e) =>
                handleFilterChange('status', e.target.value as MatchStatus | '')
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <Typeahead<Investor>
              options={investors}
              value={
                filters.investor_id
                  ? investors.find((i) => i.id === filters.investor_id) ?? null
                  : null
              }
              onChange={(inv) =>
                setFilters((prev) => ({
                  ...prev,
                  investor_id: inv ? inv.id : undefined,
                }))
              }
              getKey={(inv) => inv.id}
              getLabel={(inv) => inv.full_name || inv.firm_name || `(investor #${inv.id})`}
              getSecondary={(inv) => inv.firm_name || undefined}
              emptyLabel="All investors"
              placeholder="Type investor name…"
            />

            <Typeahead<Project>
              options={projects}
              value={
                filters.project_id
                  ? projects.find((p) => p.id === filters.project_id) ?? null
                  : null
              }
              onChange={(p) =>
                setFilters((prev) => ({
                  ...prev,
                  project_id: p ? p.id : undefined,
                }))
              }
              getKey={(p) => p.id}
              getLabel={(p) => p.project_name || `(project #${p.id})`}
              emptyLabel="All projects"
              placeholder="Type project name…"
            />


            <div>
              <label className="text-xs text-gray-600 block mb-1">
                Min score: {filters.min_score ?? 0}
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={filters.min_score ?? 0}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    min_score: parseInt(e.target.value, 10) || undefined,
                  }))
                }
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading matches...
          </div>
        ) : matches.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <GitMerge className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No matches found</h2>
            <p className="text-sm">
              Try generating matches for an investor or project to get started.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
              <span>{matches.length} match{matches.length === 1 ? '' : 'es'}</span>
              <label className="inline-flex items-center gap-2">
                <span>Sort:</span>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value as any)}
                  className="border border-gray-200 rounded px-2 py-1 bg-white text-xs"
                >
                  <option value="score_desc">Score (high → low)</option>
                  <option value="score_asc">Score (low → high)</option>
                  <option value="investor_asc">Investor name (A → Z)</option>
                  <option value="investor_desc">Investor name (Z → A)</option>
                  <option value="target_asc">Target name (A → Z)</option>
                  <option value="target_desc">Target name (Z → A)</option>
                  <option value="created_desc">Newest first</option>
                  <option value="created_asc">Oldest first</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {sortedMatches.map((m) => (
                <MatchCard key={m.id} match={m} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Generate dialog */}
      {genOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !generating && setGenOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Generate matches</h2>
                <p className="text-xs text-gray-600">
                  Pick a scope and the engine will score the relevant pairings.
                </p>
              </div>
              <button
                onClick={() => !generating && setGenOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Scope
                </label>
                <div className="flex gap-2">
                  {(['investor', 'project', 'pair'] as GenerateScope[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setGenScope(s)}
                      className={`px-3 py-1.5 text-sm rounded-lg border ${
                        genScope === s
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {s === 'investor'
                        ? 'Per investor'
                        : s === 'project'
                        ? 'Per project'
                        : 'Specific pair'}
                    </button>
                  ))}
                </div>
              </div>

              {(genScope === 'investor' || genScope === 'pair') && (
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Investor
                  </label>
                  <select
                    value={genInvestorId ?? ''}
                    onChange={(e) =>
                      setGenInvestorId(e.target.value ? parseInt(e.target.value, 10) : undefined)
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select investor...</option>
                    {investors.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.full_name}
                        {inv.firm_name ? ` (${inv.firm_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(genScope === 'project' || genScope === 'pair') && (
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Project
                  </label>
                  <select
                    value={genProjectId ?? ''}
                    onChange={(e) =>
                      setGenProjectId(e.target.value ? parseInt(e.target.value, 10) : undefined)
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select project...</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.project_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setGenOpen(false)}
                disabled={generating}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual match creation modal — operator picks an investor + a
          company explicitly, score is computed using the same engine,
          row stamped source="manual" so it shows the purple badge. */}
      {manualOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 flex items-center justify-center p-4"
          onClick={() => !manualSubmitting && setManualOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Manual match</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Pick an investor + company. Score is computed automatically
                  using the same engine; the match is flagged{' '}
                  <span className="text-purple-700 font-medium">Manual</span>.
                </p>
              </div>
              <button
                onClick={() => !manualSubmitting && setManualOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-50"
                disabled={manualSubmitting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Investor
                </label>
                <Typeahead<Investor>
                  options={investors}
                  value={manualInvestor}
                  onChange={setManualInvestor}
                  getKey={(inv) => inv.id}
                  getLabel={(inv) => inv.full_name || inv.firm_name || `(investor #${inv.id})`}
                  getSecondary={(inv) => inv.firm_name || undefined}
                  placeholder="Type to search investors..."
                  emptyLabel="Pick an investor..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Company
                </label>
                <Typeahead<{ id: number; name: string; industry?: string }>
                  options={manualCompanyOptions}
                  value={manualCompany}
                  onChange={setManualCompany}
                  getKey={(c) => c.id}
                  getLabel={(c) => c.name}
                  getSecondary={(c) => c.industry}
                  placeholder="Type to search companies..."
                  emptyLabel="Pick a company..."
                />
                {manualCompanyOptions.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">Loading companies...</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Notes (optional - appended to talking points)
                </label>
                <textarea
                  rows={2}
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                  placeholder="Why are you pairing these two manually?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => !manualSubmitting && setManualOpen(false)}
                disabled={manualSubmitting}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-white rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitManualMatch}
                disabled={manualSubmitting || !manualInvestor || !manualCompany}
                className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
              >
                {manualSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <span className="mr-1.5 text-base leading-none">✦</span>
                    Create match
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MatchesPage;
